/* eslint-disable react-hooks/set-state-in-effect */
import { faTimes, faXmark } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Alert,
  Box,
  Button,
  Drawer,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { Family } from "../../../models";
import { toQueryString } from "../../../utils/uri";

interface GenerateTaxReportDrawerProps {
  open: boolean;
  families: Family[];
  defaultFamilyId?: number;
  onClose: () => void;
}

function getInitialYear(): number {
  return new Date().getFullYear();
}

export function GenerateTaxReportDrawer(props: GenerateTaxReportDrawerProps) {
  const { open, families, defaultFamilyId, onClose } = props;
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));

  const [selectedFamilyIds, setSelectedFamilyIds] = useState<number[]>([]);
  const [year, setYear] = useState<number>(getInitialYear());
  const [lf, setLf] = useState<string>("");
  const [errorMessage, setErrorMessage] = useState<string>("");

  useEffect(() => {
    if (!open) {
      return;
    }

    if (defaultFamilyId && families.some((x) => x.id === defaultFamilyId)) {
      setSelectedFamilyIds([defaultFamilyId]);
    } else {
      setSelectedFamilyIds([]);
    }

    setYear(getInitialYear());
    setLf("");
    setErrorMessage("");
  }, [open, defaultFamilyId, families]);

  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: currentYear - 2011 }, (_, index) => 2012 + index).reverse();
  }, []);

  const sortedFamilies = useMemo(() => {
    return [...families].sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));
  }, [families]);

  const handleGenerateReport = () => {
    setErrorMessage("");
    if (selectedFamilyIds.length === 0) {
      setErrorMessage("Select at least one family.");
      return;
    }

    if (!year) {
      setErrorMessage("Tax year is required.");
      return;
    }

    const normalizedLf = lf.trim();

    selectedFamilyIds.forEach((familyId) => {
      const query = toQueryString({
        familyId,
        year,
        lf: normalizedLf || undefined,
      });

      const reportUrl = `${window.location.origin}/print/tax-report?${query}`;
      window.open(reportUrl, "_blank", "noopener,noreferrer");
    });

    onClose();
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{ zIndex: 1550 }}
      slotProps={{
        paper: {
          sx: {
            width: isSmallScreen ? "100%" : "400px",
            maxWidth: "100%",
            zIndex: 1550,
            display: "flex",
            flexDirection: "column",
          },
        },
      }}
    >
      <Stack sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2, height: "100%", overflow: "auto" }}>
        {/* Header with title and close button */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ flexShrink: 0 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Tax Report
          </Typography>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              width: 44,
              height: 44,
            }}
          >
            <Box sx={{ fontSize: "1.25rem" }}>
              <FontAwesomeIcon icon={faXmark} />
            </Box>
          </IconButton>
        </Stack>

        <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
          Select one or more families. A separate report tab opens for each selected family.
        </Typography>

        {errorMessage && (
          <Alert severity="error" sx={{ flexShrink: 0 }}>
            {errorMessage}
          </Alert>
        )}

        <FormControl fullWidth sx={{ flexShrink: 0 }} variant="filled">
          <InputLabel>Families</InputLabel>
          <Select
            variant="filled"
            multiple
            value={selectedFamilyIds}
            onChange={(event) => {
              const values = event.target.value as Array<number | string>;
              setSelectedFamilyIds(values.map((x) => Number(x)));
            }}
            renderValue={(selected) => {
              const selectedIds = selected as number[];
              return sortedFamilies
                .filter((family) => selectedIds.includes(family.id))
                .map((family) => family.name)
                .join(", ");
            }}
          >
            {sortedFamilies.map((family) => (
              <MenuItem key={family.id} value={family.id}>
                {family.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth sx={{ flexShrink: 0 }} variant="filled">
          <InputLabel>Tax Year</InputLabel>
          <Select variant="filled" value={year} label="Tax Year" onChange={(event) => setYear(Number(event.target.value))}>
            {yearOptions.map((value) => (
              <MenuItem key={value} value={value}>
                {value}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          variant="filled"
          label="TIN Last 4 (optional)"
          value={lf}
          onChange={(event) => {
            const value = event.target.value.replace(/\D/g, "").slice(0, 4);
            setLf(value);
          }}
          inputProps={{ maxLength: 4 }}
          helperText="If provided, this replaces xxxx in 20-429xxxx"
          sx={{ flexShrink: 0 }}
        />

        <Box sx={{ flexGrow: 1, minHeight: 8 }} />

        <Stack gap={2} sx={{ flexShrink: 0 }}>
          <Button variant="contained" onClick={handleGenerateReport} sx={{ height: 50, width: "100%" }}>
            Generate Report
          </Button>
          <Button variant="outlined" onClick={onClose} sx={{ height: 50, width: "100%" }} startIcon={<FontAwesomeIcon icon={faTimes} />}>
            Cancel
          </Button>
        </Stack>
      </Stack>
    </Drawer>
  );
}
